import type { BrowserOptions, ErrorEvent, EventHint } from "@sentry/react";
import { toast } from "react-toastify";

type TracesSamplerSamplingContext = Parameters<NonNullable<BrowserOptions["tracesSampler"]>>[0];

// ── Connectivity detection ───────────────────────────────────────────────────

/**
 * A genuine client-side connectivity failure: a true network-level fetch rejection —
 * the request never reached the server. A ky/backend TimeoutError does NOT qualify —
 * the request reached our backend fine and it was merely slow to respond; see
 * `isBackendTimeoutError` for that case (EN-841).
 */
export function isConnectivityError(error: unknown): boolean {
    if (!error) return false;
    const msg = (error as any)?.message?.toLowerCase() ?? "";
    const name = (error as any)?.name ?? "";
    const rawCode = (error as any)?.code;
    const code = typeof rawCode === "string" ? rawCode.toLowerCase() : "";

    return (
        msg.includes("failed to fetch") ||
        msg.includes("load failed") ||
        msg.includes("network error") ||
        msg.includes("network request failed") ||
        msg.includes("connection refused") ||
        msg.includes("connection reset") ||
        msg.includes("dns resolution failed") ||
        msg.includes("host unreachable") ||
        msg.includes("no internet connection") ||
        msg.includes("offline") ||
        code === "network_error" ||
        code === "connection_error" ||
        (name === "TypeError" && msg.includes("fetch")) ||
        name === "NetworkError"
    );
}

/**
 * A request that reached our backend but timed out waiting for a response (ky's
 * TimeoutError, whose message is "Request timed out: ..."). Backend/integration
 * slowness, not a client connectivity problem. Deliberately narrow (name check, or the
 * exact "timed out" phrasing) so it never overlaps with `isConnectivityError`'s
 * network-rejection strings — keeps Sentry's `connectivity` bucket and the
 * connectivity toast meaningful (EN-841).
 */
export function isBackendTimeoutError(error: unknown): boolean {
    if (!error) return false;
    const msg = (error as any)?.message?.toLowerCase() ?? "";
    const name = (error as any)?.name ?? "";
    const rawCode = (error as any)?.code;
    const code = typeof rawCode === "string" ? rawCode.toLowerCase() : "";

    return name === "TimeoutError" || msg.includes("timed out") || code === "timeout_error";
}

// ── Combined classification ──────────────────────────────────────────────────

export type NetworkErrorCategory = "connectivity" | "backend_latency" | "code_bug";

/** Classifies `error` once so callers don't re-run `isConnectivityError`/`isBackendTimeoutError` separately. */
export function classifyNetworkError(error: unknown): NetworkErrorCategory {
    if (isConnectivityError(error)) return "connectivity";
    if (isBackendTimeoutError(error)) return "backend_latency";
    return "code_bug";
}

/** Shows the toast matching `category`, if any (no-op for "code_bug"). */
export function notifyNetworkError(category: NetworkErrorCategory): void {
    if (category === "connectivity") showConnectivityToast();
    else if (category === "backend_latency") showBackendLatencyToast();
}

// ── Secret-field redaction ───────────────────────────────────────────────────

/**
 * Key names treated as credential-shaped, matched case-insensitively as a substring against every
 * object key walked below (PR review, 2026-10-07: `EzyvetCredentialsUpload.tsx`'s ky `HTTPError`
 * carries the raw request body — including a `client_secret` — on `err.request`/`err.options`.
 * Nothing in this app's Sentry config reads those properties today, but relying on "nothing reads
 * this today" as the only safeguard is fragile — this exists so a FUTURE config change (e.g. adding
 * `extraErrorDataIntegration`, which serializes an Error's own enumerable properties into
 * `event.contexts` as a structured object) can't silently start leaking it). Broad by design — a
 * safety net, not a precise allowlist, so a near-miss key name (`clientSecret`, `api_secret`,
 * `refresh_token`) is still caught.
 */
const SECRET_KEY_RE = /secret|password|token|api[_-]?key/i;

/**
 * Deep-copy `value`, replacing the value under any credential-shaped key (see {@link SECRET_KEY_RE})
 * with a fixed marker rather than omitting it, so the event's shape stays legible. Depth-capped and
 * cycle-safe, mirroring {@link redactUuidsDeep}; never throws.
 */
export function redactSecretFieldsDeep(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
    if (depth > 6) return value;
    if (value === null || typeof value !== "object") return value;
    if (seen.has(value as object)) return "[Circular]";
    seen.add(value as object);
    if (Array.isArray(value)) return value.map((v) => redactSecretFieldsDeep(v, depth + 1, seen));
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = SECRET_KEY_RE.test(k) ? "[REDACTED]" : redactSecretFieldsDeep(v, depth + 1, seen);
    }
    return out;
}

// ── UUID redaction ───────────────────────────────────────────────────────────

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Stable, non-reversible 8-hex surrogate for a UUID (FNV-1a 32-bit). */
function uuidSurrogate(uuid: string): string {
    let h = 0x811c9dc5;
    for (let i = 0; i < uuid.length; i++) {
        h ^= uuid.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return `uuid_${(h >>> 0).toString(16).padStart(8, "0")}`;
}

/** Replace any UUID substring in a string with its surrogate. */
export function redactUuidsInString(s: string): string {
    return s.replace(UUID_RE, (m) => uuidSurrogate(m));
}

/**
 * Return a deep copy of `value` with every UUID substring (in strings, object
 * values, and array elements) replaced by a stable non-reversible surrogate.
 * Depth-capped and cycle-safe; never throws.
 */
export function redactUuidsDeep(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
    if (depth > 6) return value;
    if (typeof value === "string") return redactUuidsInString(value);
    if (value === null || typeof value !== "object") return value;
    if (seen.has(value as object)) return "[Circular]";
    seen.add(value as object);
    if (Array.isArray(value)) return value.map((v) => redactUuidsDeep(v, depth + 1, seen));
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = redactUuidsDeep(v, depth + 1, seen);
    }
    return out;
}

// ── beforeSend factory ───────────────────────────────────────────────────────

interface BeforeSendOptions {
    isProductionEnv: boolean;
    getIsPageUnloading: () => boolean;
    appVersion: string;
    shouldSendError: (userId: string | undefined, message: string | undefined) => boolean;
}

export function buildBeforeSend(opts: BeforeSendOptions) {
    return (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
        // Non-production: log locally, never send
        if (!opts.isProductionEnv) {
            console.error(hint.originalException, event.extra);
            return null;
        }

        const ex = hint.originalException;

        // Drop AbortErrors — expected request cancellations
        if (ex instanceof DOMException && ex.name === "AbortError") return null;
        if (ex instanceof Error && ex.name === "AbortError") return null;

        // Drop network cancellations during page unload (CacheBuster reload)
        const errorValue = event.exception?.values?.[0]?.value ?? "";
        const isNetworkCancel =
            errorValue.includes("Failed to fetch") ||
            errorValue.includes("Load failed") ||
            errorValue.includes("NetworkError");
        if (isNetworkCancel && opts.getIsPageUnloading()) return null;

        // Drop Browsing Topics API browser deprecation notices
        if (errorValue.includes("Browsing Topics API")) return null;

        // Drop Chrome extension context invalidated (expected on extension reload)
        if (errorValue.includes("[PageBridge]") && errorValue.includes("Extension context invalidated")) return null;

        // Drop Clipboard NotAllowedError (browser security restriction, not actionable)
        if (ex instanceof Error && ex.name === "NotAllowedError" && ex.message.toLowerCase().includes("clipboard"))
            return null;

        // Attach app_version tag
        event.tags = { ...event.tags, app_version: opts.appVersion };

        // Tag connectivity/backend-timeout errors — do not overwrite if already set at call site
        if (!event.tags.errorCategory) {
            const category = classifyNetworkError(ex);
            if (category !== "code_bug") {
                event.tags.errorCategory = category;
            }
            if (category === "connectivity") {
                event.contexts = {
                    ...event.contexts,
                    connectionInfo: {
                        online: navigator.onLine,
                        connectionType: (navigator as any).connection?.effectiveType ?? "unknown",
                    },
                };
            }
        }

        // Dedup: suppress repeated errors from the same user within cooldown window
        if (event.user && event.exception) {
            const message = event.exception.values?.[0]?.value;
            const userId = event.user.id != null ? String(event.user.id) : undefined;
            if (!opts.shouldSendError(userId, message)) return null;
        }

        // Redact credential-shaped fields (client_secret, password, tokens, api keys — see
        // SECRET_KEY_RE) and UUIDs (patient record ids, document ids, etc.) before sending.
        // Centralized here so every call site is covered, present or future, rather than trusting
        // each one to never pass a raw error/request object through. Never let scrubbing throw
        // away a real event.
        try {
            if (event.extra) event.extra = redactUuidsDeep(redactSecretFieldsDeep(event.extra)) as typeof event.extra;
            if (event.contexts)
                event.contexts = redactUuidsDeep(redactSecretFieldsDeep(event.contexts)) as typeof event.contexts;
            if (event.breadcrumbs) {
                event.breadcrumbs = event.breadcrumbs.map((b) => ({
                    ...b,
                    message: b.message ? redactUuidsInString(b.message) : b.message,
                    data: b.data ? (redactUuidsDeep(redactSecretFieldsDeep(b.data)) as typeof b.data) : b.data,
                }));
            }
            if (event.message) event.message = redactUuidsInString(event.message);
            const values = event.exception?.values;
            if (values) {
                for (const v of values) {
                    if (v.value) v.value = redactUuidsInString(v.value);
                }
            }
        } catch {
            // Scrubbing must not block error reporting; send the (unredacted) event rather than nothing.
        }

        return event;
    };
}

// ── Connectivity toast ───────────────────────────────────────────────────────

let lastConnectivityToastAt = 0;
const CONNECTIVITY_TOAST_COOLDOWN_MS = 30_000;

export function showConnectivityToast(): void {
    const now = Date.now();
    if (now - lastConnectivityToastAt < CONNECTIVITY_TOAST_COOLDOWN_MS) return;
    lastConnectivityToastAt = now;
    toast.warning("Connection issue — check your network and try again.", {
        toastId: "connectivity-error",
    });
}

export function resetConnectivityToastCooldown(): void {
    lastConnectivityToastAt = 0;
}

// ── Backend latency toast ────────────────────────────────────────────────────

let lastBackendLatencyToastAt = 0;
const BACKEND_LATENCY_TOAST_COOLDOWN_MS = 30_000;

export function showBackendLatencyToast(): void {
    const now = Date.now();
    if (now - lastBackendLatencyToastAt < BACKEND_LATENCY_TOAST_COOLDOWN_MS) return;
    lastBackendLatencyToastAt = now;
    toast.warning("Something's taking longer than usual. We're on it — please try again in a moment.", {
        toastId: "backend-latency",
    });
}

export function resetBackendLatencyToastCooldown(): void {
    lastBackendLatencyToastAt = 0;
}

// ── Traces sampling (EN-1326) ────────────────────────────────────────────────

/**
 * Transaction sample rate per environment. Production was at 1.0 (~1.6M pageloads/14d),
 * which exhausted the org's span quota and stopped all span ingestion (EN-1326).
 */
export function getTracesSampleRate(env: string | undefined): number {
    if (env === "production") return 0.1;
    if (env === "preview" || env === "staging") return 0.25;
    return 1.0;
}

/** `tracesSampler` that honors an incoming parent sampling decision, else uses the env rate. */
export function buildTracesSampler(env: string | undefined) {
    const rate = getTracesSampleRate(env);
    return (samplingContext: TracesSamplerSamplingContext): number => samplingContext.inheritOrSampleWith(rate);
}
